import { updateRestrictionPointLayer } from './restrictionPointLayer'
import { useEffect, useRef } from 'react'
import type {
  Map as MapboxMap,
  Popup,
  GeoJSONSource,
  MapMouseEvent,
} from 'mapbox-gl'
import { TFunction } from 'i18next'
import {
  getUniqueRdppfTypes,
  getRestrictionsByType,
  createRestrictionsGeoJSON,
  getRestrictionColor,
  formatRestrictionTooltip,
  isRestrictionLayer,
} from '../../../../molecules/AnalysisMap/utils/restrictionLayerHelpers'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'

interface UseRestrictionsLayersProps {
  map: React.MutableRefObject<MapboxMap | null>
  mapLoaded: boolean
  popup: React.MutableRefObject<Popup | null>
  styleLoadedCount: number
  otherMapOptions: any
  /** Optional translation function for restriction zone names */
  tRestrictions?: TFunction
}

/**
 * Hook to handle restriction polygon layers and hover tooltips
 * Creates dynamic layers for each RDPPF type present in the restrictions data
 */
export const useRestrictionsLayers = ({
  map,
  mapLoaded,
  popup,
  styleLoadedCount,
  otherMapOptions,
  tRestrictions,
}: UseRestrictionsLayersProps) => {
  // Track the restrictions array reference per source so we can skip
  // source.setData() when only the hidden-ids filter changed. Without this,
  // every toggle re-runs the effect (otherMapOptions is a fresh ref each
  // render) and re-uploads the same GeoJSON, which causes visible jank.
  const lastRestrictionsRef = useRef<Map<string, any[]>>(new Map())

  // Handle restriction polygon layers
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }
    // Bail out if the style is mid-transition (e.g. switching sections
    // swaps the base style). addSource/addLayer throw "Style is not done
    // loading" otherwise; the styleLoadedCount dep will retrigger us
    // once the new style is ready.
    if (!map.current.isStyleLoaded()) {
      return
    }

    // Check if restrictions section is active
    const activeSection = (otherMapOptions as any).activeSection
    const isRestrictionsSection = activeSection === 'restrictions'
    const restrictionsData = (otherMapOptions as any).sectionData?.restrictions
    const layerStates = (otherMapOptions as any).demographicsLayerStates // Reuse same prop for consistency
    const hiddenIdsRaw =
      ((otherMapOptions as any).sectionData?.hiddenRestrictionIds as
        (string | number)[] | undefined) || []
    // `properties.id` is a string in our GeoJSON — match on the property
    // (well-optimized in Mapbox) rather than on feature-state ['id'].
    const hiddenIds = hiddenIdsRaw.map((id) => String(id))
    const baseHiddenFilter =
      hiddenIds.length === 0
        ? null
        : (['!', ['in', ['get', 'id'], ['literal', hiddenIds]]] as any)

    if (
      !isRestrictionsSection ||
      !restrictionsData ||
      restrictionsData.length === 0
    ) {
      // Remove restriction sources and layers if not in restrictions section
      let existingRestrictionLayers: string[] = []
      try {
        const style = map.current.getStyle()
        if (style?.layers) {
          existingRestrictionLayers = style.layers
            .filter((layer) => isRestrictionLayer(layer.id))
            .map((layer) => layer.id)
        }
      } catch {
        // Style not loaded yet, skip cleanup
        return
      }

      existingRestrictionLayers.forEach((layerId) => {
        if (map.current!.getLayer(layerId)) {
          try {
            map.current!.removeLayer(layerId)
          } catch (error) {
            console.warn(
              `Failed to remove restriction layer ${layerId}:`,
              error
            )
          }
        }
      })

      // Remove all restriction sources
      existingRestrictionLayers.forEach((layerId) => {
        const sourceId = `${layerId}-source`
        if (map.current!.getSource(sourceId)) {
          try {
            map.current!.removeSource(sourceId)
          } catch (error) {
            console.warn(
              `Failed to remove restriction source ${sourceId}:`,
              error
            )
          }
        }
      })
      lastRestrictionsRef.current.clear()

      return
    }

    // Restrictions section is active - create layers dynamically for each RDPPF type
    const rdppfTypes = getUniqueRdppfTypes(restrictionsData)

    rdppfTypes.forEach((rdppfType) => {
      const layerId = `restrictions-${rdppfType.id}`
      const sourceId = `${layerId}-source`

      // Get restrictions for this type
      const typeRestrictions = getRestrictionsByType(
        restrictionsData,
        rdppfType.id
      )
      const dataChanged =
        lastRestrictionsRef.current.get(sourceId) !== restrictionsData
      const geoJson = dataChanged
        ? createRestrictionsGeoJSON(typeRestrictions)
        : null

      // Get layer config (fall back to base config if specific type config doesn't exist)
      const layerConfig =
        getLayerConfig(layerId) || getLayerConfig('restrictions-base')
      const layerState = layerStates?.[layerId] || {
        enabled: layerConfig?.defaultEnabled || true,
        opacity: layerConfig?.defaultOpacity || 30,
      }

      // Add or update GeoJSON source
      if (!map.current!.getSource(sourceId)) {
        map.current!.addSource(sourceId, {
          type: 'geojson',
          data: geoJson || createRestrictionsGeoJSON(typeRestrictions),
          // Generate feature IDs if not present - required for feature-state
          generateId: false, // We provide IDs in the GeoJSON
        })
        lastRestrictionsRef.current.set(sourceId, restrictionsData)
      } else if (dataChanged && geoJson) {
        // Update existing source data only when the underlying restrictions
        // array changed — this is the expensive call.
        const source = map.current!.getSource(sourceId) as GeoJSONSource
        source.setData(geoJson)
        lastRestrictionsRef.current.set(sourceId, restrictionsData)
      }

      updateRestrictionPointLayer(
        map.current!,
        layerId,
        sourceId,
        getRestrictionColor(rdppfType.id, rdppfType.name),
        layerState.enabled,
        layerState.opacity / 100,
        baseHiddenFilter
      )

      // Add or update polygon fill layer
      if (!map.current!.getLayer(layerId)) {
        const fillColor = getRestrictionColor(rdppfType.id, rdppfType.name)

        map.current!.addLayer({
          id: layerId,
          type: 'fill',
          source: sourceId,
          ...(baseHiddenFilter ? { filter: baseHiddenFilter } : {}),
          layout: {
            visibility: layerState.enabled ? 'visible' : 'none',
          },
          paint: {
            'fill-color': fillColor,
            'fill-opacity': layerState.opacity / 100,
          },
        })

        // Add outline layer for better visibility
        const outlineLayerId = `${layerId}-outline`
        if (!map.current!.getLayer(outlineLayerId)) {
          map.current!.addLayer({
            id: outlineLayerId,
            type: 'line',
            source: sourceId,
            ...(baseHiddenFilter ? { filter: baseHiddenFilter } : {}),
            layout: {
              visibility: layerState.enabled ? 'visible' : 'none',
            },
            paint: {
              'line-color': fillColor,
              'line-width': 2.5,
              'line-opacity': 1,
            },
          })
        }

        // Add hover layer for interactive highlight
        const hoverLayerId = `${layerId}-hover`
        if (!map.current!.getLayer(hoverLayerId)) {
          map.current!.addLayer({
            id: hoverLayerId,
            type: 'line',
            source: sourceId,
            ...(baseHiddenFilter ? { filter: baseHiddenFilter } : {}),
            layout: {
              visibility: layerState.enabled ? 'visible' : 'none',
            },
            paint: {
              'line-color': fillColor,
              'line-width': 4,
              'line-opacity': [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                1,
                0,
              ],
            },
          })
        }

        // Numeric label layer — renders the instance number at each
        // polygon's visual center so the map can be cross-referenced
        // with the list. Only features with `instanceNumber` get a
        // label (single-instance types get no number).
        const labelLayerId = `${layerId}-label`
        if (!map.current!.getLayer(labelLayerId)) {
          const labelFilter = baseHiddenFilter
            ? (['all', ['has', 'instanceNumber'], baseHiddenFilter] as any)
            : (['has', 'instanceNumber'] as any)
          map.current!.addLayer({
            id: labelLayerId,
            type: 'symbol',
            source: sourceId,
            filter: labelFilter,
            layout: {
              visibility: layerState.enabled ? 'visible' : 'none',
              'text-field': ['to-string', ['get', 'instanceNumber']],
              'text-size': 14,
              'text-allow-overlap': true,
              'text-ignore-placement': true,
              'symbol-placement': 'point',
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': fillColor,
              'text-halo-width': 2,
            },
          })
        }
      } else {
        // Update existing layer
        map.current!.setLayoutProperty(
          layerId,
          'visibility',
          layerState.enabled ? 'visible' : 'none'
        )
        map.current!.setPaintProperty(
          layerId,
          'fill-opacity',
          layerState.opacity / 100
        )
        map.current!.setFilter(layerId, baseHiddenFilter)

        // Update outline layer
        const outlineLayerId = `${layerId}-outline`
        if (map.current!.getLayer(outlineLayerId)) {
          map.current!.setLayoutProperty(
            outlineLayerId,
            'visibility',
            layerState.enabled ? 'visible' : 'none'
          )
          map.current!.setFilter(outlineLayerId, baseHiddenFilter)
        }

        // Update hover layer
        const hoverLayerId = `${layerId}-hover`
        if (map.current!.getLayer(hoverLayerId)) {
          map.current!.setLayoutProperty(
            hoverLayerId,
            'visibility',
            layerState.enabled ? 'visible' : 'none'
          )
          map.current!.setFilter(hoverLayerId, baseHiddenFilter)
        }

        // Update label layer
        const labelLayerId = `${layerId}-label`
        if (map.current!.getLayer(labelLayerId)) {
          map.current!.setLayoutProperty(
            labelLayerId,
            'visibility',
            layerState.enabled ? 'visible' : 'none'
          )
          const labelFilter = baseHiddenFilter
            ? (['all', ['has', 'instanceNumber'], baseHiddenFilter] as any)
            : (['has', 'instanceNumber'] as any)
          map.current!.setFilter(labelLayerId, labelFilter)
        }
      }
    })

    // Remove any restriction layers that are no longer needed
    const currentLayerIds = rdppfTypes.map((type) => `restrictions-${type.id}`)

    // Only get base restriction layers (not outline or hover variants)
    let existingRestrictionLayers: string[] = []
    try {
      const style = map.current.getStyle()
      if (style?.layers) {
        existingRestrictionLayers = style.layers
          .filter((layer) => {
            const id = layer.id
            return (
              id.startsWith('restrictions-') &&
              !id.endsWith('-outline') &&
              !id.endsWith('-hover') &&
              !id.endsWith('-label') &&
              !id.endsWith('-points')
            )
          })
          .map((layer) => layer.id)
      }
    } catch {
      // Style not loaded yet, skip cleanup
      return
    }

    existingRestrictionLayers.forEach((layerId) => {
      if (!currentLayerIds.includes(layerId)) {
        // Remove base layer
        if (map.current!.getLayer(layerId)) {
          map.current!.removeLayer(layerId)
        }
        // Remove outline layer
        const outlineLayerId = `${layerId}-outline`
        if (map.current!.getLayer(outlineLayerId)) {
          map.current!.removeLayer(outlineLayerId)
        }
        // Remove hover layer
        const hoverLayerId = `${layerId}-hover`
        if (map.current!.getLayer(hoverLayerId)) {
          map.current!.removeLayer(hoverLayerId)
        }
        // Remove label layer
        const labelLayerId = `${layerId}-label`
        if (map.current!.getLayer(labelLayerId)) {
          map.current!.removeLayer(labelLayerId)
        }
        if (map.current!.getLayer(`${layerId}-points`))
          map.current!.removeLayer(`${layerId}-points`)
        // Remove source
        const sourceId = `${layerId}-source`
        if (map.current!.getSource(sourceId)) {
          map.current!.removeSource(sourceId)
        }
        lastRestrictionsRef.current.delete(sourceId)
      }
    })
  }, [
    mapLoaded,
    styleLoadedCount,
    (otherMapOptions as any).activeSection,
    (otherMapOptions as any).sectionData?.restrictions,
    (otherMapOptions as any).sectionData?.hiddenRestrictionIds,
    (otherMapOptions as any).demographicsLayerStates,
  ])

  // Setup hover tooltips for restriction layers
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const restrictionsData = otherMapOptions.sectionData?.restrictions
    if (!restrictionsData || restrictionsData.length === 0) return

    // Get all enabled restriction layers
    const rdppfTypes = getUniqueRdppfTypes(restrictionsData)
    const enabledRestrictionLayers = rdppfTypes
      .flatMap((type) => [
        `restrictions-${type.id}`,
        `restrictions-${type.id}-outline`,
        `restrictions-${type.id}-points`,
      ])
      .filter((layerId) => {
        const layerState =
          otherMapOptions.demographicsLayerStates?.[
            layerId.replace(/-(outline|points)$/, '')
          ]
        const isEnabled = layerState?.enabled ?? true // Default to true if state doesn't exist yet
        const layerExists = !!map.current!.getLayer(layerId)
        return isEnabled && layerExists
      })

    if (enabledRestrictionLayers.length === 0) return

    // Track currently hovered feature
    let hoveredFeatureId: string | number | null = null
    let hoveredLayerId: string | null = null

    // Handle mouse move to show tooltip and hover effect
    const handleMouseMove = (e: MapMouseEvent) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: enabledRestrictionLayers,
      })

      if (features.length > 0 && popup.current) {
        const feature = features[0]
        const tooltipHtml = formatRestrictionTooltip(
          feature.properties,
          tRestrictions
        )

        // Update hover state for highlighting
        if (feature.id !== undefined && feature.layer?.id) {
          // Remove previous hover state
          if (hoveredFeatureId !== null && hoveredLayerId) {
            try {
              map.current!.setFeatureState(
                { source: `${hoveredLayerId}-source`, id: hoveredFeatureId },
                { hover: false }
              )
            } catch (error) {
              console.warn(
                '[Hover] Failed to remove previous hover state:',
                error
              )
            }
          }

          // Set new hover state
          // Ensure feature ID is a number for feature-state to work
          hoveredFeatureId =
            typeof feature.id === 'string'
              ? parseInt(feature.id, 10)
              : feature.id
          hoveredLayerId =
            feature.layer?.id?.replace(/-(outline|points)$/, '') ?? null

          try {
            map.current!.setFeatureState(
              { source: `${hoveredLayerId}-source`, id: hoveredFeatureId },
              { hover: true }
            )
          } catch (error) {
            console.error('[Hover] Failed to set hover state:', error)
          }
        }

        // Change cursor to pointer
        map.current!.getCanvas().style.cursor = 'pointer'

        // Show popup
        popup.current
          .setLngLat(e.lngLat)
          .setHTML(
            `<div style="padding: 8px 12px; font-size: 13px;">
              ${tooltipHtml}
            </div>`
          )
          .addTo(map.current!)
      } else {
        // Remove hover state
        if (hoveredFeatureId !== null && hoveredLayerId) {
          map.current!.setFeatureState(
            { source: `${hoveredLayerId}-source`, id: hoveredFeatureId },
            { hover: false }
          )
          hoveredFeatureId = null
          hoveredLayerId = null
        }

        // Reset cursor
        map.current!.getCanvas().style.cursor = ''
        popup.current?.remove()
      }
    }

    // Handle mouse leave to hide tooltip and clear hover state
    const handleMouseLeave = () => {
      // Remove hover state
      if (hoveredFeatureId !== null && hoveredLayerId) {
        map.current!.setFeatureState(
          { source: `${hoveredLayerId}-source`, id: hoveredFeatureId },
          { hover: false }
        )
        hoveredFeatureId = null
        hoveredLayerId = null
      }

      map.current!.getCanvas().style.cursor = ''
      popup.current?.remove()
    }

    // Add event listeners for all enabled layers
    enabledRestrictionLayers.forEach((layerId) => {
      map.current!.on('mousemove', layerId, handleMouseMove)
      map.current!.on('mouseleave', layerId, handleMouseLeave)
    })

    // Cleanup
    return () => {
      if (map.current) {
        enabledRestrictionLayers.forEach((layerId) => {
          map.current!.off('mousemove', layerId, handleMouseMove)
          map.current!.off('mouseleave', layerId, handleMouseLeave)
        })
      }
      popup.current?.remove()
    }
  }, [
    mapLoaded,
    styleLoadedCount,
    otherMapOptions.sectionData?.restrictions,
    otherMapOptions.demographicsLayerStates,
    tRestrictions,
  ])

  // Handle restriction clicks for auto-expand in list
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const restrictionsData = otherMapOptions.sectionData?.restrictions
    if (!restrictionsData || restrictionsData.length === 0) return

    const setSelectedItemId = otherMapOptions.setSelectedItemId
    if (!setSelectedItemId) return

    // Get all enabled restriction layers
    const rdppfTypes = getUniqueRdppfTypes(restrictionsData)
    const enabledRestrictionLayers = rdppfTypes
      .flatMap((type) => [
        `restrictions-${type.id}`,
        `restrictions-${type.id}-outline`,
        `restrictions-${type.id}-points`,
      ])
      .filter((layerId) => {
        const layerState =
          otherMapOptions.demographicsLayerStates?.[
            layerId.replace(/-(outline|points)$/, '')
          ]
        const isEnabled = layerState?.enabled ?? true // Default to true if state doesn't exist yet
        const layerExists = !!map.current!.getLayer(layerId)
        return isEnabled && layerExists
      })

    if (enabledRestrictionLayers.length === 0) return

    // Handle click on restriction
    const handleClick = (e: MapMouseEvent) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: enabledRestrictionLayers,
      })

      if (features.length > 0) {
        const feature = features[0]
        const restrictionId = feature.properties?.id

        if (restrictionId && setSelectedItemId) {
          setSelectedItemId(restrictionId)
        }
      }
    }

    // Add click listeners for all enabled layers
    enabledRestrictionLayers.forEach((layerId) => {
      map.current!.on('click', layerId, handleClick)
    })

    // Cleanup
    return () => {
      if (map.current) {
        enabledRestrictionLayers.forEach((layerId) => {
          map.current!.off('click', layerId, handleClick)
        })
      }
    }
  }, [
    mapLoaded,
    styleLoadedCount,
    otherMapOptions.sectionData?.restrictions,
    otherMapOptions.demographicsLayerStates,
    otherMapOptions.setSelectedItemId,
  ])
}
